/**
 * UI switches — code constants, flipped by editing this file (no env var, no
 * rebuild flag). Read them with a strict `=== true`, so a switch that is set
 * to anything else, or removed from this file, means OFF.
 */

/**
 * USE_WEB_APP_CALENDAR — which calendar EVERY date field in both portals
 * opens (docs/knowledge/calendars.md).
 *
 *   true            the traveller app's calendar (components/calendar —
 *                   react-calendar, brand discs, tinted range band, live hover
 *                   preview). Each field keeps its own input box; only the
 *                   popup changes. A start / end pair (From–To, Check-in /
 *                   Check-out, Sales / Travel / Blackout Start–End …) opens
 *                   ONE range calendar from either box; a single date opens
 *                   a single-date one. The hotel Modify modal keeps its
 *                   two-half card (HotelDateRangePicker).
 *   false / absent  the AntD calendar everywhere, under the same boxes
 *                   (components/fields/AntDateTextField, AntDateField): value
 *                   shown "10 Oct 26"; a start / end pair is two linked AntD
 *                   calendars (range paint, picking the start opens the end);
 *                   kept inside a modal;
 *                   date + time is one AntD box with time columns; the
 *                   partner reports keep AntD's RangePicker; the Modify
 *                   modal shows StayDateRangeFields.
 *
 * Either way every calendar is the compact one of the hotel Modify modal,
 * and a start / end pair hands over like the manual hotel form: picking the
 * start opens the end's calendar under the End box when the end is empty.
 * Time pickers are not affected either way. Both follow the portal's
 * branding colour for the selected days and hover.
 */
export const USE_WEB_APP_CALENDAR = true;

/**
 * WEB_APP_CALENDAR_EXPAND — how the web-app calendar (USE_WEB_APP_CALENDAR)
 * opens in the hotel Modify modal.
 *
 *   true            inside that hotel's card, under the dates: the card grows
 *                   smoothly to fit the calendar and shrinks back when it
 *                   closes (slide / expand animation)
 *   false / absent  a floating calendar over the card, compact, opening
 *                   upward inside the modal
 *
 * Applies to the web-app calendar only — the AntD calendar always floats.
 */
export const WEB_APP_CALENDAR_EXPAND = false;

/**
 * SHOW_API_STATUS_BADGE — the QA-only status dot on sidebar nav items in both
 * portals (constants/apiStatusBadge.js; each item's `apiStatus` in the two
 * navigation configs picks the colour).
 *
 *   true            green dot = the screen's APIs are QA-verified, yellow = in
 *                   progress, on every nav item that carries an `apiStatus`
 *   false / absent  no dots: nav items show icon + label only
 */
export const SHOW_API_STATUS_BADGE = false;

/**
 * FLIGHT_MODIFY_AUTOMATE — Manual vs Automate in the flight Modify modal
 * (SMB-4379). Stands in for the backend's automate flag until it exists.
 *
 *   true            the modal offers Automate: search the supplier, pick one
 *                   of the returned flights, check availability + price, quote.
 *                   Search and check use SAMPLE data until the APIs land
 *                   (Api/flightModifyAutomateAPI.js).
 *   false / absent  the modal offers Enter Details Manually.
 */
export const FLIGHT_MODIFY_AUTOMATE = true;

/**
 * SHOW_HOTEL_MANUAL_BOARD_AND_ROOMS — the hotel Modify modal's Manual request
 * form, "Room upgrade" section.
 *
 *   true            it also asks for the board type (Room Only, Bed &
 *                   Breakfast, …) and the number of rooms, sent as the
 *                   stay's `boardType` / `noOfRooms`
 *   false / absent  room name only; the request never carries a board or
 *                   room-count change
 */
export const SHOW_HOTEL_MANUAL_BOARD_AND_ROOMS = true;

/**
 * HOTEL_MANUAL_QUOTE_GST_BY_RATE — which GST contract the hotel manual quote
 * is SENT in (APImdFiles/HOTEL_MANUAL_REQUEST_API.md, "GST in a quote"). The
 * form works GST out the same way either way: each fee × its layer's rate
 * (the booking's `paymentDetails.gstRates`, else typed), half-up to 2 places.
 *
 *   true            the 30 Oct contract (PR #3265): `zenvoyaGstRate` /
 *                   `partnerGstRate` and every fee with its own GST line
 *                   (zenvoyaMarkupGst, …, 0 when none); the API checks each
 *                   line (GST_MISMATCH)
 *   false / absent  the contract before it: GST as one amount per layer,
 *                   `zenvoyaGst` / `partnerGst`, no rates or lines
 *
 * Match the backend that is deployed: the older one refuses the line keys,
 * the newer one requires them.
 */
export const HOTEL_MANUAL_QUOTE_GST_BY_RATE = true;

/**
 * USE_HOTEL_MANUAL_QUOTE_BOOKING_BASIS — DISPLAY ONLY (user, 2026-10-29): the
 * "Retained margin · fixed" block in the CRM's hotel manual quote form
 * (create / edit: the Modify / Cancel modals' Quote step, Create / Replace
 * quote, Complete's own numbers).
 *
 *   true            the block shows the booking's retained margin (fees and
 *                   their GST); the inputs read "Add charges"
 *   false / absent  the block is hidden
 *
 * The figures never change with it: with the booking's `paymentDetails` the
 * base is `totalPaid` (locked), the retained margin is deducted as a charge
 * and a cancel deducts `promoAvailable`, either way.
 */
export const USE_HOTEL_MANUAL_QUOTE_BOOKING_BASIS = false;

/**
 * SHOW_HOTEL_MANUAL_QUOTE_RETAINED_SPLIT — how View Quote (and Complete's
 * accepted quote) shows a hotel manual quote's charges.
 *
 *   true            split: the booking's retained margin as its own
 *                   "Retained margin · fixed" block and only what the quote
 *                   added as "Added charges" / "Added deductions"; a modify
 *                   reads previously paid → charges (the retained margin) →
 *                   remaining → new stay charge (the stay + the charges
 *                   added) → due / credit
 *   false / absent  merged, as the API stores it (each fee = retained +
 *                   added); a modify reads quote total → previously paid →
 *                   due / refund
 *
 * Split only when the booking has `paymentDetails` and the quote holds at
 * least the retained figures; otherwise it shows merged.
 */
export const SHOW_HOTEL_MANUAL_QUOTE_RETAINED_SPLIT = true;

/**
 * QUOTE_UI — the layout of a hotel manual request in the booking's Servicing
 * Requests table (both portals): its expanded row and its Flow Status pill.
 *
 *   "old"           the same as the activity and flight requests: Flow
 *                   Progress (In Progress → Checking Supplier → Quote Ready →
 *                   Customer Review → Accepted → Re-quote → Confirmed →
 *                   Complete), the "Waiting for your review" bar, Before /
 *                   After, the quotes, Request Timeline
 *                   (hotelManualRequest/ManualRequestExpandedOld)
 *   "new"           the live API's own: status, waiting-on, round and
 *                   priority chips, requested change beside every quote
 *                   version, the completion, notes & files, the timeline and
 *                   one action row (hotelManualRequest/ManualRequestExpandedNew)
 *
 * Anything but "new" reads as "old". The data and actions are the same live
 * API either way.
 */
export const QUOTE_UI = "old";

/**
 * QUOTE_WALLET_PART — the "Wallet part of the …" box in the CRM's hotel manual
 * quote form (the Modify / Cancel Quote step, Create / Replace quote,
 * Complete's own numbers): the wallet side of the amount due, of a modify's
 * credit, or of a cancel's refund — the rest settled offline.
 *
 *   "show"          the box shows
 *   "hide"          hidden: nothing is sent for it, so the backend's default
 *                   applies (all of the due / refund on the wallet, as much as
 *                   the wallet paid)
 *
 * Anything but "show" hides it. A quote that already carries a split still
 * shows it in View Quote.
 */
export const QUOTE_WALLET_PART = "hide";

/**
 * HOTEL_MANUAL_QUOTE_CALCULATE — how the CRM writes a hotel manual quote (the
 * Modify / Cancel Quote step, Create / Replace quote). Spec 30 Oct (late):
 * the FE does no money arithmetic (APImdFiles/HOTEL_MANUAL_REQUEST_API.md,
 * "Calculate").
 *
 *   true            the API works it out: calculate/prefill fills the form
 *                   (supplier penalty, free window, the 4 fees the rules
 *                   suggest; modify: the new stay's supplier price), Calculate
 *                   runs calculate/quote, and the quote goes out as its
 *                   `send.calculation` (hotelManualRequest/ManualCalcQuoteForm).
 *                   A modify has no amendment fee — the 4 fees are the new
 *                   stay's own markup / service fee
 *   false / absent  the hand-typed form (ManualQuoteForm) — every fee typed, GST
 *                   worked out here; the GST / booking-basis / split / wallet
 *                   switches above apply to it only
 *
 * Match the backend that is deployed: the calculate routes are new, and once
 * they are live the hand-typed modify's amendment keys are refused.
 */
export const HOTEL_MANUAL_QUOTE_CALCULATE = true;

/**
 * USE_MOCK_FLIGHT_MODIFY_REQUESTS — where FLIGHT manual modification requests
 * go (Modify modal → "Manual request", the Request tab's flight requests and
 * quotes). A separate flow from the hotel one.
 *
 *   true            a local mock (Api/flightModifyRequestMock.js, kept in
 *                   localStorage per booking) that plays the backend
 *   false / absent  the real endpoints, flight/modify-request/*
 *                   (APImdFiles/FLIGHT_MODIFY_REQUEST_API.md) — set this once
 *                   app-backend ships them
 */
export const USE_MOCK_FLIGHT_MODIFY_REQUESTS = true;

/**
 * USE_MOCK_ACTIVITY_MODIFY_REQUESTS — NOT READ by the activity flows any more:
 * since 2026-10-29 activity Modify and Cancel → "Manual request" both go to the
 * backend's manual-request API (Api/activityManualRequestAPI). Only
 * Api/hotelModifyRequestAPI's legacy activity_* families (unused) still check it.
 */
export const USE_MOCK_ACTIVITY_MODIFY_REQUESTS = true;

/**
 * HIDE_CANCELLED_HOTELS_WITHOUT_USER_IN_BILLING — the booking detail Billing
 * tab (both portals) and the hotel rows /get sends as cancelled with no
 * `cancelledBy` (no user is recorded as cancelling them — e.g. the stay a
 * Modify replaced; helpers/hotelRowVisibility.js).
 *
 *   true            the Fare Breakdown leaves their fare rows out, as the
 *                   Itinerary does — they are listed in the Transaction
 *                   History instead (user, 2026-11-05)
 *   false / absent  the Fare Breakdown lists them too
 *
 * Either way the Itinerary (and the print) leaves them out of the cards and
 * Guest Details and shows them in its collapsed Booking History, and the
 * Transaction History lists them. The Billing summary (Total Amount, Markup,
 * …) stays what /get sends — it still counts those rows.
 */
export const HIDE_CANCELLED_HOTELS_WITHOUT_USER_IN_BILLING = true;

